import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./shad/select";
import { UseFetchLanguages } from "../hooks/useLanguages";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setLanguage } from "../features/userSlice";
import api, { doneLanguages } from "../utils";
import apiRoutes from "../apiRoutes";
import { useMutation } from "@tanstack/react-query";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const dispatch = useAppDispatch();
  const { language } = useAppSelector((state) => state.user);

  const { languages } = UseFetchLanguages();

  const availableLanguages = languages?.filter((item) =>
    doneLanguages.includes(item.short_code)
  );

  const switchLanguage = async (code: string) => {
    const res = await api.post(apiRoutes.settings, {
      type: "languages",
      language: availableLanguages?.find((item) => item.short_code === code)
        ?.id,
    });
    return res.data;
  };

  const { mutate } = useMutation({
    mutationFn: switchLanguage,
  });

  const handleLanguageChange = (newLang: string) => {
    setIsPending(true);
    i18n.changeLanguage(newLang).then(() => {
      setIsPending(false);
      dispatch(setLanguage(newLang));
      mutate(newLang);
    });
  };

  useEffect(() => {
    i18n.changeLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center space-x-2">
      <Select
        defaultValue={language ?? i18n.language}
        onValueChange={handleLanguageChange}
        disabled={isPending}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Select Language" />
        </SelectTrigger>
        <SelectContent>
          {availableLanguages?.map((language) => (
            <SelectItem key={language.id} value={language.short_code}>
              <div className="flex items-center gap-2">
                <img
                  src={language.image}
                  alt={language.short_code}
                  className="h-4 w-6"
                />
                {language.language}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isPending && (
        <div className="text-sm text-gray-500">Changing language...</div>
      )}
    </div>
  );
}
